/** A cut lands between words, so it can land on a comma. Trailing punctuation
 *  before an ellipsis reads as a typo rather than as a cut. */
const TRAILING_PUNCTUATION = /[\s,;:.!?-]+$/;

/** The words of a paragraph, with the whitespace between them thrown away. */
export function words(text: string): string[] {
  return text.trim().split(/\s+/).filter(Boolean);
}

/**
 * The first `count` words, tidied for an ellipsis to follow.
 *
 * Carries no ellipsis of its own: the caller renders "… Read more" as one run,
 * so the ellipsis belongs to the toggle rather than to the prose.
 */
export function cutToWords(text: string, count: number): string {
  const all = words(text);
  if (count >= all.length) return text.trim();
  return all.slice(0, Math.max(count, 0)).join(' ').replace(TRAILING_PUNCTUATION, '');
}

/**
 * How many words fit, by binary search over `fits`.
 *
 * A search rather than a characters-per-line estimate, because the answer
 * depends on the glyphs: "Illinois" and "wwwwwwww" are the same length and not
 * the same width, so any constant is wrong for one of them. Roughly eight
 * measurements for a paragraph rather than one per word.
 *
 * `fits(n)` answers whether the first n words are short enough, and must be
 * monotonic — true for every count below the answer — which holds for text that
 * only grows as words are added.
 */
export function fittingWords(total: number, fits: (count: number) => boolean): number {
  if (total <= 0) return 0;
  if (fits(total)) return total;

  let low = 0;
  let high = total;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (fits(mid)) low = mid;
    else high = mid - 1;
  }
  return low;
}
